import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import React from 'react';

export interface MobileEntityField {
    label: React.ReactNode;
    value: React.ReactNode;
    fullWidth?: boolean;
}

export interface MobileEntityCardProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    status?: React.ReactNode;
    fields?: MobileEntityField[];
    actions?: React.ReactNode;
    ariaLabel?: string;
}

/**
 * Touch-friendly representation of one row from a desktop data grid.
 *
 * Keep domain-specific actions and formatting at the call site. This primitive
 * only supplies a consistent hierarchy, metadata layout, and minimum hit area.
 */
const MobileEntityCard = ({
    title,
    subtitle,
    status,
    fields = [],
    actions,
    ariaLabel,
}: MobileEntityCardProps) => (
    <Card component="article" variant="outlined" aria-label={ariaLabel} sx={{ width: '100%' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" component="h2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {status && <Box sx={{ flex: '0 0 auto' }}>{status}</Box>}
                </Stack>

                {fields.length > 0 && (
                    <Grid container component="dl" spacing={1.25} sx={{ m: 0 }}>
                        {fields.map((field, index) => (
                            <Grid item xs={field.fullWidth ? 12 : 6} key={index} sx={{ minWidth: 0 }}>
                                <Typography component="dt" variant="caption" color="text.secondary">
                                    {field.label}
                                </Typography>
                                <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.25, overflowWrap: 'anywhere' }}>
                                    {field.value}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {actions && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                        alignItems="center"
                        flexWrap="wrap"
                        role="group"
                        aria-label="Actions"
                        sx={{
                            mx: -0.5,
                            mb: -0.5,
                            '& .MuiIconButton-root, & .MuiButtonBase-root': {
                                minWidth: 44,
                                minHeight: 44,
                            },
                        }}
                    >
                        {actions}
                    </Stack>
                )}
            </Stack>
        </CardContent>
    </Card>
);

export default MobileEntityCard;
